/**
 * Block B Phase 2.D (2026-06-01) — Migration Wizard UI.
 *
 * 6-step wizard для конфігурації міграції з 1С УТП:
 *   1. Дата міграції (з рекомендацією 31 грудня)
 *   2. Профіль (small/medium/enterprise/custom)
 *   3. Кастомізація глибин (тільки для custom)
 *   4. Підтвердження
 *   5. Iнструкція для 1С обробки (Run ID + параметри)
 *   6. Чекання на 1С (live status)
 *
 * Phase 1 Review Decisions (власник 2026-06-01) — інтегровано
 * через PROFILE_DEFAULTS у api/migrationRuns.ts.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { orgs } from '@/api/catalogs';
import {
  migrationRuns,
  PROFILE_DEFAULTS,
  type MigrationDepthConfig,
  type MigrationProfile,
  type MigrationRun,
  type OsvReconciliation,
  type ReconciliationFinding,
  type ReconciliationReport,
  type ReconciliationSource,
} from '@/api/migrationRuns';
import { migration as migrationApi } from '@/api/migration';
import DatePopover from '@/components/DatePopover';
import { quarantine } from '@/api/quarantine';

type Step = 1 | 2 | 3 | 4 | 5 | 6;

interface WizardState {
  step: Step;
  organization_id: string;
  migration_date: string;
  profile: MigrationProfile;
  depth_config: MigrationDepthConfig;
  run_id: string | null;
}

const INITIAL_STATE: WizardState = {
  step: 1,
  organization_id: '',
  migration_date: '',
  profile: 'medium_business',
  depth_config: PROFILE_DEFAULTS.medium_business,
  run_id: null,
};

export default function MigrationWizardPage() {
  const qc = useQueryClient();
  const [state, setState] = useState<WizardState>(INITIAL_STATE);

  const orgList = useQuery({
    queryKey: ['orgs'],
    queryFn: () => orgs.list({ limit: 200 }),
  });

  const createRun = useMutation({
    mutationFn: () =>
      migrationRuns.create({
        organization_id: state.organization_id,
        migration_date: state.migration_date,
        profile: state.profile,
        depth_config:
          state.profile === 'custom' ? state.depth_config : undefined,
      }),
    onSuccess: (run) => {
      setState((s) => ({ ...s, run_id: run.id, step: 5 }));
      qc.invalidateQueries({ queryKey: ['migration-runs'] });
    },
  });

  // Block B 2.D fix (2026-06-01): обробка 409 — підвантажуємо існуючі runs
  // щоб користувач міг продовжити або скасувати existing run.
  const activeRunsForOrg = useQuery({
    queryKey: ['migration-runs-active', state.organization_id],
    queryFn: () =>
      state.organization_id
        ? migrationRuns.list({ organization_id: state.organization_id })
        : Promise.resolve([]),
    enabled: !!state.organization_id && createRun.isError,
  });

  const removeRun = useMutation({
    mutationFn: (id: string) => migrationRuns.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['migration-runs-active'] });
      createRun.reset();
    },
  });

  function continueExistingRun(runId: string) {
    setState((s) => ({ ...s, run_id: runId, step: 5 }));
    createRun.reset();
  }

  const startRun = useMutation({
    mutationFn: (id: string) => migrationRuns.start(id),
    onSuccess: () => setState((s) => ({ ...s, step: 6 })),
  });

  // Block B Phase 3: bulk-POST мутація
  const bulkPostRun = useMutation({
    mutationFn: (id: string) => migrationRuns.bulkPost(id),
    onSuccess: () => {
      // Live polling step 6 підхопить новий стан
      qc.invalidateQueries({ queryKey: ['migration-run', state.run_id] });
    },
  });

  const liveRun = useQuery({
    queryKey: ['migration-run', state.run_id],
    queryFn: () =>
      state.run_id ? migrationRuns.get(state.run_id) : Promise.resolve(null),
    enabled: state.step === 6 && state.run_id !== null,
    refetchInterval: state.step === 6 ? 3000 : false,
  });

  // Block B Phase 3 auto-fire (2026-06-03): коли 1С обробка завершила
  // передачу даних (status=IMPORTING), автоматично запускаємо bulk-POST.
  // Це закриває TODO з day-24-частина-1 — раніше wizard зависав у
  // BULK_POSTING 50%, користувач мусив натискати кнопку вручну (а спочатку
  // ще й чекати, не знаючи що це його роль).
  //
  // Idempotency: triggeredForRunId ref гарантує що auto-fire спрацьовує
  // рівно один раз для кожного run_id.
  //
  // Trigger condition (2026-06-05 fix): IMPORTING ОR BULK_POSTING.
  // 1С обробка endpoint /migration/import сам переключає
  // IMPORTING → BULK_POSTING після успіху, тому wizard polling часто
  // бачить вже BULK_POSTING (а не IMPORTING). Раніше auto-fire чекав
  // тільки IMPORTING і пропускав випадок — wizard зависав на 50%.
  const autoBulkPostTriggered = useRef<string | null>(null);
  // 2026-07-17 fix: авто-fire ЛИШЕ на ЖИВОМУ переході статусу (порожньо/
  // WIZARD/READY → IMPORTING/BULK_POSTING) під час цієї сесії поллінгу.
  // Раніше відкриття сторінки на «залежаному» BULK_POSTING (після невдалої
  // вигрузки статус лишається від минулого імпорту) стріляло повторно і
  // проводило СТАРІ чернетки між вигрузками (прецедент: 303 виробництва
  // без рахунків з рядків 1С).
  const prevStatus = useRef<string | null>(null);
  useEffect(() => {
    if (!state.run_id || !liveRun.data) return;
    const s = liveRun.data.status;
    const prev = prevStatus.current;
    prevStatus.current = s;
    // Fire ЛИШЕ на переході У BULK_POSTING (імпорт ЗАВЕРШЕНО — endpoint
    // /import сам ставить цей статус після успіху). На IMPORTING НЕ
    // стріляємо: імпорт великого пакета триває хвилини, bulk-post
    // паралельно проводив перші секції і завершував run до кінця імпорту
    // (гонка, прецедент 2026-07-18: 58 з 2800 доків).
    if (s !== 'BULK_POSTING') return;
    if (prev === null || prev === 'BULK_POSTING') return;
    if (autoBulkPostTriggered.current === state.run_id) return;
    if (bulkPostRun.isPending) return;
    autoBulkPostTriggered.current = state.run_id;
    bulkPostRun.mutate(state.run_id);
  }, [state.run_id, liveRun.data, bulkPostRun]);

  function setProfile(p: MigrationProfile) {
    if (p === 'custom') {
      setState((s) => ({ ...s, profile: p }));
    } else {
      setState((s) => ({
        ...s,
        profile: p,
        depth_config: PROFILE_DEFAULTS[p],
      }));
    }
  }

  function nextStep() {
    setState((s) => ({ ...s, step: ((s.step + 1) as Step) }));
  }

  function prevStep() {
    setState((s) => ({ ...s, step: ((s.step - 1) as Step) }));
  }

  function reset() {
    setState(INITIAL_STATE);
  }

  return (
    <div style={{ maxWidth: 900, margin: '1rem auto', padding: '1rem' }}>
      <h2>Майстер міграції з 1С</h2>
      <ProgressBar step={state.step} />

      {state.step === 1 && (
        <Step1Date
          value={state.migration_date}
          onChange={(d) => setState((s) => ({ ...s, migration_date: d }))}
          orgId={state.organization_id}
          onOrgChange={(id) =>
            setState((s) => ({ ...s, organization_id: id }))
          }
          orgList={orgList.data?.items ?? []}
          onNext={nextStep}
        />
      )}

      {state.step === 2 && (
        <Step2Profile
          profile={state.profile}
          onProfileChange={setProfile}
          onNext={nextStep}
          onBack={prevStep}
        />
      )}

      {state.step === 3 && (
        <Step3CustomDepth
          config={state.depth_config}
          onChange={(c) => setState((s) => ({ ...s, depth_config: c }))}
          onNext={nextStep}
          onBack={prevStep}
          profile={state.profile}
        />
      )}

      {state.step === 4 && (
        <Step4Confirm
          state={state}
          orgName={
            orgList.data?.items.find((o) => o.id === state.organization_id)
              ?.name ?? '—'
          }
          onConfirm={() => createRun.mutate()}
          onBack={prevStep}
          isPending={createRun.isPending}
          error={createRun.error}
          activeRuns={activeRunsForOrg.data ?? []}
          onContinueExisting={continueExistingRun}
          onRemoveRun={(id) => removeRun.mutate(id)}
          removeRunPending={removeRun.isPending}
        />
      )}

      {state.step === 5 && state.run_id && (
        <Step5Instructions
          runId={state.run_id}
          migrationDate={state.migration_date}
          organizationId={state.organization_id}
          depthConfig={state.depth_config}
          onStart={() => state.run_id && startRun.mutate(state.run_id)}
          onBack={prevStep}
          isPending={startRun.isPending}
        />
      )}

      {state.step === 6 && (
        <Step6Waiting
          run={liveRun.data ?? null}
          onReset={reset}
          onBulkPost={() => state.run_id && bulkPostRun.mutate(state.run_id)}
          bulkPostPending={bulkPostRun.isPending}
        />
      )}
    </div>
  );
}

function ProgressBar({ step }: { step: Step }) {
  const steps = [
    'Дата',
    'Профіль',
    'Глибини',
    'Підтвердження',
    'Iнструкція',
    'Прогрес',
  ];
  return (
    <div style={{ display: 'flex', gap: '0.5rem', margin: '1rem 0' }}>
      {steps.map((title, i) => {
        const active = step === i + 1;
        const done = step > i + 1;
        return (
          <div
            key={i}
            style={{
              flex: 1,
              padding: '0.5rem',
              background: done ? '#d4f4d4' : active ? '#fff9cc' : '#eee',
              border: active ? '2px solid #06c' : '1px solid #ccc',
              borderRadius: 4,
              textAlign: 'center',
              fontSize: '0.85em',
            }}
          >
            {i + 1}. {title}
          </div>
        );
      })}
    </div>
  );
}

interface Step1Props {
  value: string;
  onChange: (d: string) => void;
  orgId: string;
  onOrgChange: (id: string) => void;
  orgList: { id: string; name: string }[];
  onNext: () => void;
}

function Step1Date({ value, onChange, orgId, onOrgChange, orgList, onNext }: Step1Props) {
  const today = new Date().toISOString().slice(0, 10);
  const canNext = orgId && value;
  return (
    <fieldset style={{ padding: '1rem', border: '1px solid #ccc' }}>
      <legend>Крок 1: Організація і дата міграції</legend>

      <div className="field" style={{ marginBottom: '1rem' }}>
        <label>Організація *</label>
        <select value={orgId} onChange={(e) => onOrgChange(e.target.value)}>
          <option value="">— Оберіть організацію —</option>
          {orgList.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label>Дата міграції *</label>
        <DatePopover
          value={value}
          onChange={(v) => onChange(v)}
        />
        <div style={{ marginTop: '0.5rem' }}>
          <button type="button" onClick={() => onChange(today)}>
            Сьогодні (рекомендовано)
          </button>
        </div>
        <small style={{ color: 'var(--muted)', display: 'block', marginTop: '0.5rem' }}>
          Це дата ПЕРЕХОДУ на сервіс. Документи вигружаються за N місяців
          НАЗАД від цієї дати (глибина — крок 3). Дата, на яку беруться
          початкові залишки, вказується окремо у формі 1С («Дата залишків»).
          Дата в минулому відсіче всі новіші документи — не ставте сюди дату
          залишків.
        </small>
      </div>

      <div style={{ marginTop: '1.5rem', textAlign: 'right' }}>
        <button
          className="primary"
          disabled={!canNext}
          onClick={onNext}
        >
          Далі →
        </button>
      </div>
    </fieldset>
  );
}

interface Step2Props {
  profile: MigrationProfile;
  onProfileChange: (p: MigrationProfile) => void;
  onNext: () => void;
  onBack: () => void;
}

function Step2Profile({ profile, onProfileChange, onNext, onBack }: Step2Props) {
  return (
    <fieldset style={{ padding: '1rem', border: '1px solid #ccc' }}>
      <legend>Крок 2: Профіль бізнесу</legend>

      <p style={{ color: 'var(--muted)' }}>
        Профіль визначає глибину історії що мігрується. Ви завжди можете
        обрати «Власні налаштування» для точного контролю.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <ProfileOption
          value="small_business"
          current={profile}
          onChange={onProfileChange}
          title="🏢 Малий бізнес"
          description="ФОП або ТОВ 1-15 людей. Зарплата 12м, FIFO 24м. Швидка міграція."
        />
        <ProfileOption
          value="medium_business"
          current={profile}
          onChange={onProfileChange}
          title="🏬 Середній бізнес (рекомендовано)"
          description="ТОВ 15-50 людей. Зарплата 24м, FIFO 36м. Збалансовано."
        />
        <ProfileOption
          value="enterprise"
          current={profile}
          onChange={onProfileChange}
          title="🏭 Велике підприємство / виробництво"
          description="50+ людей, виробництво. Зарплата 24м, FIFO 60м (старі партії), ПДВ 12м."
        />
        <ProfileOption
          value="custom"
          current={profile}
          onChange={onProfileChange}
          title="⚙️ Власні налаштування"
          description="Точний контроль глибини для кожного домену окремо."
        />
      </div>

      <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
        <button onClick={onBack}>← Назад</button>
        <button className="primary" onClick={onNext}>
          Далі →
        </button>
      </div>
    </fieldset>
  );
}

interface ProfileOptionProps {
  value: MigrationProfile;
  current: MigrationProfile;
  onChange: (p: MigrationProfile) => void;
  title: string;
  description: string;
}

function ProfileOption({ value, current, onChange, title, description }: ProfileOptionProps) {
  const selected = current === value;
  return (
    <label
      style={{
        padding: '0.75rem',
        border: selected ? '2px solid #06c' : '1px solid #ccc',
        borderRadius: 6,
        background: selected ? '#f0f8ff' : '#fff',
        cursor: 'pointer',
      }}
    >
      <input
        type="radio"
        name="profile"
        value={value}
        checked={selected}
        onChange={() => onChange(value)}
        style={{ marginRight: '0.5rem' }}
      />
      <strong>{title}</strong>
      <div style={{ fontSize: '0.85em', color: 'var(--muted)', marginLeft: '1.5rem' }}>
        {description}
      </div>
    </label>
  );
}

interface Step3Props {
  config: MigrationDepthConfig;
  onChange: (c: MigrationDepthConfig) => void;
  onNext: () => void;
  onBack: () => void;
  profile: MigrationProfile;
}

function Step3CustomDepth({ config, onChange, onNext, onBack, profile }: Step3Props) {
  const isCustom = profile === 'custom';
  if (!isCustom) {
    // Не custom — показуємо preview і кнопку «далі»
    return (
      <fieldset style={{ padding: '1rem', border: '1px solid #ccc' }}>
        <legend>Крок 3: Глибина історії (preview)</legend>
        <p>Для обраного профілю використовуються такі глибини:</p>
        <DepthPreview config={config} />
        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
          <button onClick={onBack}>← Назад</button>
          <button className="primary" onClick={onNext}>
            Далі →
          </button>
        </div>
      </fieldset>
    );
  }

  return (
    <fieldset style={{ padding: '1rem', border: '1px solid #ccc' }}>
      <legend>Крок 3: Власна глибина історії (custom)</legend>
      <p style={{ color: 'var(--muted)' }}>
        Кожне поле — кількість місяців історії документів. 0 = тільки залишки.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
        <DepthSlider
          label="Зарплата (місяців)"
          value={config.payroll_months}
          onChange={(v) => onChange({ ...config, payroll_months: v })}
          max={120}
          help="≥12м потрібно для жовтневих відпусток"
        />
        <DepthSlider
          label="Товари FIFO (місяців)"
          value={config.fifo_months}
          onChange={(v) => onChange({ ...config, fifo_months: v })}
          max={120}
          help="≥24м для типової торгівлі, ≥60м для виробництва"
        />
        <DepthSlider
          label="Касові/банк. виписки"
          value={config.cash_bank_months}
          onChange={(v) => onChange({ ...config, cash_bank_months: v })}
          max={120}
          help="12м — стандарт"
        />
        <DepthSlider
          label="Податкові накладні"
          value={config.tax_invoices_months}
          onChange={(v) => onChange({ ...config, tax_invoices_months: v })}
          max={120}
          help="24м для аудит-готовності"
        />
        <DepthSlider
          label="ПДВ пропорційне"
          value={config.vat_months}
          onChange={(v) => onChange({ ...config, vat_months: v })}
          max={120}
          help="0 = декларації у 1С; 12м для пропорційного ПДВ ст.199"
        />
        <DepthSlider
          label="Авансові звіти"
          value={config.advance_reports_months}
          onChange={(v) => onChange({ ...config, advance_reports_months: v })}
          max={120}
          help="12м потрібно для ФОП ЗСО річного звіту"
        />
        <DepthSlider
          label="Кадрові накази"
          value={config.hr_orders_months}
          onChange={(v) => onChange({ ...config, hr_orders_months: v })}
          max={120}
          help="0 = тільки активні працівники; >0 = архів наказів (накази, ЦПХ)"
        />
        <DepthSlider
          label="Аудит ОЗ-операцій"
          value={config.assets_audit_months}
          onChange={(v) => onChange({ ...config, assets_audit_months: v })}
          max={120}
          help="Snapshot карток + N місяців документних операцій"
        />
      </div>

      <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
        <button onClick={onBack}>← Назад</button>
        <button className="primary" onClick={onNext}>
          Далі →
        </button>
      </div>
    </fieldset>
  );
}

interface DepthSliderProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  max: number;
  help: string;
}

function DepthSlider({ label, value, onChange, max, help }: DepthSliderProps) {
  return (
    <div className="field">
      <label>
        {label}: <strong>{value}м</strong>
      </label>
      <input
        type="range"
        min={0}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: '100%' }}
      />
      <small style={{ color: 'var(--muted)' }}>{help}</small>
    </div>
  );
}

function DepthPreview({ config }: { config: MigrationDepthConfig }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody>
        <DepthRow label="Зарплата" value={`${config.payroll_months} місяців`} />
        <DepthRow label="Товари FIFO" value={`${config.fifo_months} місяців`} />
        <DepthRow label="Касові/банк. виписки" value={`${config.cash_bank_months} місяців`} />
        <DepthRow label="Податкові накладні" value={`${config.tax_invoices_months} місяців`} />
        <DepthRow label="ПДВ пропорційне" value={`${config.vat_months} місяців`} />
        <DepthRow label="Авансові звіти" value={`${config.advance_reports_months} місяців`} />
        <DepthRow
          label="Основні засоби"
          value={`Snapshot ${config.assets_snapshot_only ? '✓' : '✗'} + ${config.assets_audit_months}м документів аудиту`}
        />
        <DepthRow
          label="Замовлення"
          value={config.orders_active_only ? 'Тільки активні' : 'Усі за період'}
        />
      </tbody>
    </table>
  );
}

function DepthRow({ label, value }: { label: string; value: string }) {
  return (
    <tr style={{ borderBottom: '1px solid #eee' }}>
      <td style={{ padding: '0.4rem 0', color: 'var(--muted)' }}>{label}</td>
      <td style={{ padding: '0.4rem 0', textAlign: 'right' }}>
        <strong>{value}</strong>
      </td>
    </tr>
  );
}

interface ActiveRunListItem {
  id: string;
  organization_id: string;
  migration_date: string;
  profile: string;
  status: string;
  progress_percent: number;
  created_at: string;
  completed_at: string | null;
}

interface Step4Props {
  state: WizardState;
  orgName: string;
  onConfirm: () => void;
  onBack: () => void;
  isPending: boolean;
  error: unknown;
  activeRuns: ActiveRunListItem[];
  onContinueExisting: (id: string) => void;
  onRemoveRun: (id: string) => void;
  removeRunPending: boolean;
}

function extractErrorDetail(error: unknown): { code: number | null; message: string } {
  if (error instanceof AxiosError) {
    const code = error.response?.status ?? null;
    const data = error.response?.data;
    let message: string;
    if (data && typeof data === 'object' && 'detail' in data) {
      const detail = (data as { detail: unknown }).detail;
      message = typeof detail === 'string' ? detail : JSON.stringify(detail);
    } else {
      message = error.message;
    }
    return { code, message };
  }
  return { code: null, message: String(error) };
}

function Step4Confirm({
  state, orgName, onConfirm, onBack, isPending, error,
  activeRuns, onContinueExisting, onRemoveRun, removeRunPending,
}: Step4Props) {
  const errorInfo = error !== null && error !== undefined ? extractErrorDetail(error) : null;
  const is409Conflict = errorInfo?.code === 409;
  // При 409 — фільтруємо тільки активні runs для поточної org
  const conflictingRuns = is409Conflict
    ? activeRuns.filter(
        (r) =>
          r.organization_id === state.organization_id &&
          !['COMPLETED', 'FAILED', 'ROLLED_BACK'].includes(r.status),
      )
    : [];

  return (
    <fieldset style={{ padding: '1rem', border: '1px solid #ccc' }}>
      <legend>Крок 4: Підтвердження</legend>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <DepthRow label="Організація" value={orgName} />
          <DepthRow label="Дата міграції" value={state.migration_date} />
          <DepthRow label="Профіль" value={state.profile} />
        </tbody>
      </table>

      <h4 style={{ marginTop: '1rem' }}>Глибини історії:</h4>
      <DepthPreview config={state.depth_config} />

      {is409Conflict && conflictingRuns.length > 0 && (
        <div
          style={{
            marginTop: '1rem',
            padding: '0.75rem',
            background: '#fff9cc',
            border: '1px solid #e5b800',
            borderRadius: 6,
          }}
        >
          <strong>⚠️ Для цієї організації вже є активна міграція.</strong>
          <p style={{ margin: '0.5rem 0' }}>
            У вас уже є незавершений wizard run для цієї організації. Ви
            можете <strong>продовжити</strong> з існуючим, або
            <strong> скасувати</strong> його щоб створити новий з нашими
            новими параметрами.
          </p>
          {conflictingRuns.map((run) => (
            <div
              key={run.id}
              style={{
                marginTop: '0.75rem',
                padding: '0.5rem',
                background: '#fff',
                borderRadius: 4,
                border: '1px solid #ddd',
              }}
            >
              <div style={{ marginBottom: '0.5rem', fontSize: '0.9em' }}>
                <strong>Дата міграції:</strong> {run.migration_date}
                {' · '}
                <strong>Профіль:</strong> {run.profile}
                {' · '}
                <strong>Статус:</strong>{' '}
                <code>{run.status}</code> ({run.progress_percent}%)
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="primary"
                  onClick={() => onContinueExisting(run.id)}
                >
                  ▶ Продовжити цей run
                </button>
                <button
                  className="danger"
                  onClick={() => {
                    if (
                      confirm(
                        `Скасувати існуючий wizard run? ` +
                        `Усі дані що були налаштовані — будуть втрачені.`,
                      )
                    ) {
                      onRemoveRun(run.id);
                    }
                  }}
                  disabled={removeRunPending}
                >
                  {removeRunPending ? 'Скасовуємо…' : '🗑 Скасувати'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {errorInfo && !is409Conflict && (
        <div
          style={{
            color: '#d33',
            marginTop: '1rem',
            padding: '0.75rem',
            background: '#fee',
            borderRadius: 6,
          }}
        >
          <strong>Помилка {errorInfo.code ?? '?'}:</strong> {errorInfo.message}
        </div>
      )}

      <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
        <button onClick={onBack} disabled={isPending}>← Назад</button>
        <button
          className="primary"
          onClick={onConfirm}
          disabled={isPending || (is409Conflict && conflictingRuns.length > 0)}
        >
          {isPending ? 'Створюємо…' : 'Створити та продовжити →'}
        </button>
      </div>
    </fieldset>
  );
}

interface Step5Props {
  runId: string;
  migrationDate: string;
  organizationId: string;
  depthConfig: MigrationDepthConfig;
  onStart: () => void;
  onBack: () => void;
  isPending: boolean;
}

/** ISO 'YYYY-MM-DD' → 'DD.MM.YYYY' для копіювання у 1С (укр. формат дати). */
function toUaDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

/**
 * Обчислює «Дату початку документів» для обробки 1С: дата міграції мінус
 * НАЙБІЛЬША глибина (у місяцях) з усіх доменів. Обробка тягне документи
 * однією датою-початком, тож беремо max — щоб гарантовано попали і товарні
 * партії FIFO (найглибші), і решта. Повертає 'YYYY-MM-DD'.
 */
function computeDocStartDate(
  migrationDate: string,
  cfg: MigrationDepthConfig,
): { date: string; maxMonths: number; driver: string } {
  const domains: { months: number; label: string }[] = [
    { months: cfg.fifo_months, label: 'товарні партії (FIFO)' },
    { months: cfg.payroll_months, label: 'зарплата' },
    { months: cfg.tax_invoices_months, label: 'податкові накладні' },
    { months: cfg.goods_months, label: 'товарні документи' },
    { months: cfg.cash_bank_months, label: 'каса/банк' },
    { months: cfg.advance_reports_months, label: 'авансові звіти' },
    { months: cfg.commission_months, label: 'комісія' },
    { months: cfg.assets_audit_months, label: 'основні засоби' },
    { months: cfg.vat_months, label: 'ПДВ' },
    { months: cfg.hr_orders_months, label: 'кадрові накази' },
  ];
  const top = domains.reduce((a, b) => (b.months > a.months ? b : a));
  const d = new Date(`${migrationDate}T00:00:00`);
  if (Number.isNaN(d.getTime()) || top.months <= 0) {
    return { date: migrationDate, maxMonths: 0, driver: top.label };
  }
  // ВАЖЛИВО: спершу day=1, ПОТIМ -months. Інакше setMonth з днем 31
  // перекидає («31 лютого» → березень). Завжди перший день місяця.
  d.setDate(1);
  d.setMonth(d.getMonth() - top.months);
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  return { date: iso, maxMonths: top.months, driver: top.label };
}

/**
 * Кнопка «копіювати» поряд зі значенням (Run ID / URL / дати у кроці 5).
 * navigator.clipboard з fallback на execCommand (старі браузери / http).
 * Показує «✓» 2 секунди після копіювання.
 */
function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // fallback для не-https / старих браузерів
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return (
    <button
      type="button"
      onClick={handleCopy}
      title={label ? `Копіювати ${label}` : 'Копіювати'}
      style={{
        marginLeft: '0.4rem', padding: '0.05rem 0.4rem', fontSize: '0.8em',
        cursor: 'pointer', verticalAlign: 'middle',
      }}
    >
      {copied ? '✓' : '📋'}
    </button>
  );
}

function Step5Instructions({
  runId, migrationDate, organizationId, depthConfig, onStart, onBack, isPending,
}: Step5Props) {
  const docStart = computeDocStartDate(migrationDate, depthConfig);
  // Адрес — з поточного origin (звідки відкрито сайт), НЕ хардкод 3-d.app:
  // dev1 → https://3-d.app, другий розробник → http://localhost:8081 (свій backend
  // через SSH-тунель). Так імпорт іде у ТУ базу, з якої відкрито візард (Норма #28).
  const importUrl = `${window.location.origin}/api/v1/migration/import`;
  return (
    <fieldset style={{ padding: '1rem', border: '1px solid #ccc' }}>
      <legend>Крок 5: Iнструкція для 1С/BAS обробки</legend>

      <div style={{ background: '#f0f8ff', padding: '1rem', borderRadius: 6, marginBottom: '1rem' }}>
        <strong>Конфігурація створена!</strong> Тепер потрібен{' '}
        <strong>міграційний токен</strong> — це ключ доступу для 1С/BAS
        обробки. Створіть його тут (можна повторно, токени накопичуються
        у списку):
      </div>

      <TokenCreator organizationId={organizationId} />

      <h3 style={{ marginTop: '1.5rem' }}>Кроки у вашій 1С/BAS:</h3>
      <ol>
        <li>Скачайте обробку з нашого сервісу:{' '}
          <a href="/migration/from-1c" target="_blank" rel="noopener noreferrer">
            Налаштування → Міграція → Завантажити обробку
          </a>
        </li>
        <li>Відкрийте у 1С/BAS через <strong>Файл → Відкрити</strong></li>
        <li>У формі обробки введіть:
          <ul>
            <li><strong>URL</strong>: <code>{importUrl}</code>
              <CopyButton value={importUrl} label="URL" /></li>
            <li><strong>Migration Run ID</strong>: <code>{runId}</code>
              <CopyButton value={runId} label="Run ID" /></li>
            <li><strong>Дата залишків</strong>: <code>{toUaDate(migrationDate)}</code>
              <CopyButton value={toUaDate(migrationDate)} label="дату залишків" /></li>
            <li>
              <strong>Дата початку документів</strong>:{' '}
              <code>{toUaDate(docStart.date)}</code>
              <CopyButton value={toUaDate(docStart.date)} label="дату початку" />
              {docStart.maxMonths > 0 && (
                <span style={{ color: 'var(--muted)', fontSize: '0.85em' }}>
                  {' '}— на {docStart.maxMonths} міс. назад (за вашим профілем
                  найглибше: {docStart.driver})
                </span>
              )}
            </li>
            <li><strong>Дата кінця документів</strong>: <code>{toUaDate(migrationDate)}</code>
              <CopyButton value={toUaDate(migrationDate)} label="дату кінця" /></li>
            <li><strong>Міграційний токен</strong>: вставте значення з блоку вище ↑</li>
          </ul>
        </li>
        <li>Натисніть <strong>«Перевірити підключення»</strong> у обробці —
          має показати ✓
        </li>
        <li>Натисніть «<strong>Вивантажити дані</strong>» у обробці</li>
        <li>Зачекайте на завершення міграції (від 5 хв до 2 годин)</li>
      </ol>

      <div style={{ background: '#fff9cc', padding: '0.75rem', borderRadius: 6, marginTop: '1rem' }}>
        ⚠️ <strong>Перед запуском обробки</strong> — натисніть «Я готовий, стартую»
        нижче. Сервіс позначить run як <code>READY</code> і чекатиме на дані з 1С/BAS.
      </div>

      <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
        <button onClick={onBack} disabled={isPending}>← Назад</button>
        <button className="primary" onClick={onStart} disabled={isPending}>
          {isPending ? 'Підготовка…' : 'Я готовий, стартую →'}
        </button>
      </div>
    </fieldset>
  );
}

interface TokenCreatorProps {
  organizationId: string;
}

function TokenCreator({ organizationId }: TokenCreatorProps) {
  const [tokenName, setTokenName] = useState('Wizard migration');
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const createMutation = useMutation({
    mutationFn: () => migrationApi.createToken({
      organization_id: organizationId,
      name: tokenName,
      expires_in_days: 30,
    }),
    onSuccess: (data) => {
      if (data.token) {
        setCreatedToken(data.token);
        setCopied(false);
      }
    },
  });

  async function handleCopy() {
    if (!createdToken) return;
    try {
      await navigator.clipboard.writeText(createdToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback: select + execCommand
    }
  }

  if (createdToken) {
    return (
      <div
        style={{
          background: '#d4f4d4',
          border: '1px solid #0a8',
          padding: '1rem',
          borderRadius: 6,
        }}
      >
        <strong>✅ Токен створено</strong>
        <p style={{ margin: '0.5rem 0', fontSize: '0.9em' }}>
          Скопіюйте і вставте у поле «Міграційний токен» у формі 1С/BAS
          обробки. <strong>Зберігається лише зараз</strong> — після
          переходу на інший крок не зможемо показати знову.
        </p>
        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            background: '#fff',
            padding: '0.5rem',
            border: '1px solid #ccc',
            borderRadius: 4,
            fontFamily: 'monospace',
            fontSize: '0.85em',
            wordBreak: 'break-all',
          }}
        >
          <span style={{ flex: 1 }}>{createdToken}</span>
          <button onClick={handleCopy} className="primary">
            {copied ? '✓ Скопійовано' : '📋 Копіювати'}
          </button>
        </div>
        <p style={{ margin: '0.5rem 0 0', fontSize: '0.85em', color: 'var(--muted)' }}>
          Термін дії: 30 днів. Якщо втратите — створіть новий тут.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        background: '#f9f9f9',
        border: '1px solid #ccc',
        padding: '1rem',
        borderRadius: 6,
      }}
    >
      <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9em' }}>
        Назва токена (для вашої довідки):
      </label>
      <input
        type="text"
        value={tokenName}
        onChange={(e) => setTokenName(e.target.value)}
        placeholder="Wizard migration"
        style={{ width: '100%', padding: '0.4rem', marginBottom: '0.5rem' }}
      />
      <button
        className="primary"
        onClick={() => createMutation.mutate()}
        disabled={createMutation.isPending || !tokenName.trim()}
      >
        {createMutation.isPending ? 'Створюю…' : '🔑 Створити токен'}
      </button>
      {createMutation.error !== null && createMutation.error !== undefined && (
        <p style={{ color: '#d33', marginTop: '0.5rem', fontSize: '0.85em' }}>
          Помилка: {String(createMutation.error)}
        </p>
      )}
    </div>
  );
}

interface Step6Props {
  run: MigrationRun | null;
  onReset: () => void;
  onBulkPost: () => void;
  bulkPostPending: boolean;
}

// Phase-3.5 (2026-06-01): людиночитані назви 1С/BAS конфігурацій.
// Дзеркало `SourceConfig` у backend schemas/migration_run.py.
const SOURCE_CONFIG_NAMES: Record<string, string> = {
  utp: '1С УТП',
  generic: '1С (інша конфігурація)',
  '1c_bp_ua_2x': '1С БП України 2.x',
  bas_bukh: 'BAS Бухгалтерія',
  ut_ua: '1С УТ / BAS УТ',
};

function Step6Waiting({ run, onReset, onBulkPost, bulkPostPending }: Step6Props) {
  // Migration Quarantine: чеклист контрагентів що потребують виправлення.
  // Hook ДО раннього return (Rules of Hooks).
  const isTerminalRun =
    run?.status === 'COMPLETED' || run?.status === 'BULK_POSTING';
  const quarantineQ = useQuery({
    queryKey: ['migration-quarantine', run?.id],
    queryFn: () => quarantine.contractors({ include_resolved: false, limit: 500 }),
    enabled: Boolean(run) && isTerminalRun,
  });

  if (!run) {
    return <p>Завантаження…</p>;
  }

  const isTerminal =
    run.status === 'COMPLETED' ||
    run.status === 'FAILED' ||
    run.status === 'ROLLED_BACK';

  // Block B Phase 3: bulk-POST доступний коли 1С обробка завершилась (IMPORTING)
  // або якщо ми у BULK_POSTING (resume).
  const canStartBulkPost = run.status === 'IMPORTING' || run.status === 'BULK_POSTING';

  // Phase-3.5: badge з detected source після першого POST з 1С обробки.
  const sourceName = run.source_config
    ? (SOURCE_CONFIG_NAMES[run.source_config] || run.source_config)
    : null;

  return (
    <fieldset style={{ padding: '1rem', border: '1px solid #ccc' }}>
      <legend>Крок 6: Прогрес міграції</legend>

      <div style={{ margin: '1rem 0' }}>
        <strong>Статус:</strong> <code>{run.status}</code>
        {sourceName && (
          <span
            style={{
              marginLeft: '0.75rem',
              padding: '0.15rem 0.5rem',
              background: '#e8f3ff',
              border: '1px solid #06c',
              borderRadius: 4,
              fontSize: '0.85em',
              color: '#06c',
            }}
            title="Джерело визначено автоматично 1С обробкою з payload"
          >
            📥 {sourceName}
          </span>
        )}
      </div>

      <div style={{ background: '#eee', borderRadius: 6, height: 24, overflow: 'hidden' }}>
        <div
          style={{
            width: `${run.progress_percent}%`,
            background: run.status === 'FAILED' ? '#d33' : run.status === 'COMPLETED' ? '#0a8' : '#06c',
            height: '100%',
            transition: 'width 0.5s',
            color: '#fff',
            textAlign: 'center',
            lineHeight: '24px',
          }}
        >
          {run.progress_percent}%
        </div>
      </div>

      {run.progress_message && (
        <p style={{ marginTop: '1rem', color: 'var(--muted)' }}>
          {run.progress_message}
        </p>
      )}

      {/* Block C Порція 2 (2026-06-01): reconciliation report —
          показуємо коли engine відпрацював (після POST з 1С). */}
      {run.reconciliation_report && (
        <ReconciliationReportSection
          runId={run.id}
          report={run.reconciliation_report}
        />
      )}

      {/* Звірка ОСВ (2026-07-19): еталонні обороти з регістра бухгалтерії
          1С vs наші проводки — коли обробка слала секцію osv_1c. */}
      {run.reconciliation_report?.osv && (
        <OsvReconciliationSection osv={run.reconciliation_report.osv} />
      )}

      {/* Block B Phase 3: коли 1С завершила (IMPORTING), показуємо стан bulk-POST.
          Auto-fire спрацьовує одразу при переході у IMPORTING (див. useEffect).
          Кнопка лишається як safety-net для resume case (статус=BULK_POSTING
          з попередньої сесії) — рідкісний edge case. */}
      {canStartBulkPost && (
        <div
          style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: '#f0f8ff',
            border: '1px solid #06c',
            borderRadius: 6,
          }}
        >
          <strong>
            {run.status === 'IMPORTING'
              ? '🚀 1С обробка передала дані. Запускаємо bulk-POST автоматично…'
              : '⏳ Bulk-POST у процесі. Перевірки балансу йдуть.'}
          </strong>
          <p style={{ margin: '0.5rem 0', fontSize: '0.9em' }}>
            Автоматично проведемо усі мігровані документи з 1С (cash, bank,
            товари, зарплата) і виконаємо перевірки балансу.
          </p>
          <p style={{ margin: '0.5rem 0', fontSize: '0.85em', color: 'var(--muted)' }}>
            <strong>⏱ Очікуваний час</strong>: від 5 хв (малий бізнес) до 30 хв
            (велике підприємство).
            <br />
            <strong>Не закривайте</strong> вкладку браузера.
          </p>
          {/* Manual fallback button: на випадок коли auto-fire не спрацював
              (resume після reload, мережева помилка mutation, тощо). */}
          <button
            className="primary"
            onClick={onBulkPost}
            disabled={bulkPostPending || run.status === 'BULK_POSTING'}
            style={{ marginTop: '0.5rem' }}
          >
            {bulkPostPending || run.status === 'BULK_POSTING'
              ? '⏳ Bulk-POST у процесі…'
              : '▶ Запустити Bulk-POST вручну'}
          </button>
        </div>
      )}

      {run.error_report !== null && (
        <div style={{ color: '#d33', padding: '0.5rem', background: '#fee', marginTop: '1rem' }}>
          <strong>Помилка:</strong>
          <pre style={{ fontSize: '0.85em' }}>
            {JSON.stringify(run.error_report, null, 2)}
          </pre>
        </div>
      )}

      {run.validation_report !== null && (
        <details style={{ marginTop: '1rem' }} open={run.status === 'COMPLETED'}>
          <summary>
            <strong>✅ Звіт міграції</strong>
          </summary>
          <pre style={{ fontSize: '0.85em', overflow: 'auto' }}>
            {JSON.stringify(run.validation_report, null, 2)}
          </pre>
        </details>
      )}

      {/* Migration Quarantine: чеклист контрагентів що потребують виправлення. */}
      {quarantineQ.data && quarantineQ.data.total > 0 && (
        <div
          style={{
            marginTop: '1rem', padding: '1rem',
            background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6,
          }}
        >
          <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 6 }}>
            ⚠ {quarantineQ.data.total} контрагент(ів) потребують виправлення
          </div>
          <div style={{ fontSize: 13, color: '#78350f', marginBottom: 10 }}>
            Реквізити цих контрагентів з 1С не відповідають стандарту. Вони
            завантажені, але заблоковані для нових документів, поки ви не
            виправите їх у довіднику «Контрагенти».
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#78350f' }}>
            {quarantineQ.data.items.slice(0, 20).map((q) => (
              <li key={q.entity_id} style={{ marginBottom: 4 }}>
                <strong>{q.contractor_name ?? q.contractor_code ?? q.entity_id}</strong>
                {': '}
                {q.issues.map((i) => i.message_uk).join('; ')}
              </li>
            ))}
            {quarantineQ.data.total > 20 && (
              <li>…і ще {quarantineQ.data.total - 20}</li>
            )}
          </ul>
          <a
            href="/contractors"
            style={{ display: 'inline-block', marginTop: 10, fontSize: 13 }}
          >
            → Перейти до довідника «Контрагенти»
          </a>
        </div>
      )}

      {run.status === 'COMPLETED' && (
        <div
          style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: '#d4f4d4',
            border: '1px solid #0a8',
            borderRadius: 6,
          }}
        >
          <strong>🎉 Міграція завершена успішно!</strong>
          <p style={{ margin: '0.5rem 0', fontSize: '0.9em' }}>
            Ваші дані доступні у сервісі. Можете починати працювати.
          </p>
          {run.reverse_export_available_until && (
            <p style={{ margin: '0.5rem 0', fontSize: '0.85em', color: 'var(--muted)' }}>
              💡 <strong>30 днів зворотньої міграції</strong>: до{' '}
              <code>{new Date(run.reverse_export_available_until).toLocaleDateString('uk')}</code>{' '}
              ви можете повернутись у 1С без обмежень.
            </p>
          )}
        </div>
      )}

      {isTerminal && (
        <div style={{ marginTop: '1.5rem' }}>
          <button onClick={onReset}>Створити новий wizard</button>
        </div>
      )}
    </fieldset>
  );
}

// ============================================================
// Block C Порція 2 (2026-06-01): Reconciliation report UI
// ============================================================

// Кольори/іконки/заголовки по джерелу.
const RECON_SOURCE_META: Record<ReconciliationSource, {
  color: string;
  bgColor: string;
  icon: string;
  title: string;
}> = {
  production_calendar: {
    color: '#06c',
    bgColor: '#e8f3ff',
    icon: '📅',
    title: 'Виробничий календар',
  },
  accounting_constants: {
    color: '#b8860b',
    bgColor: '#fff8e8',
    icon: '⚖️',
    title: 'Бухгалтерські константи',
  },
  period_date: {
    color: '#d33',
    bgColor: '#ffe8e8',
    icon: '⏱',
    title: 'Дата періоду (recalc заднім числом)',
  },
};

interface ReconciliationReportSectionProps {
  runId: string;
  report: ReconciliationReport;
}

function ReconciliationReportSection({ runId, report }: ReconciliationReportSectionProps) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(true);

  // Block C Порція 3 (2026-06-01): list уже створених overrides
  // щоб не пропонувати «Прийняти 1С» повторно.
  const overridesQuery = useQuery({
    queryKey: ['migration-overrides', runId],
    queryFn: () => migrationRuns.listOverrides(runId),
  });

  const overrideAcceptedKey = (f: ReconciliationFinding): string =>
    `${f.target_table ?? ''}|${f.document_external_id ?? ''}|${f.field}`;

  const acceptedKeys = new Set(
    (overridesQuery.data ?? []).map((o) =>
      `${o.target_table}|${o.target_id ?? ''}|${o.field_name}`,
    ),
  );

  const createOverride = useMutation({
    mutationFn: (f: ReconciliationFinding) =>
      migrationRuns.createOverride(runId, {
        target_table: f.target_table ?? 'doc.vacation_calculations',
        target_id: null,  // TODO: resolve external_id → UUID
        field_name: f.field,
        expected_value: f.our_value,
        override_value: f.their_value,
        reason: `Прийнято 1С/BAS значення з reconciliation: ${f.context}`,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['migration-overrides', runId] });
    },
  });

  const { total_findings, estimated_impact_uah, blocking_findings } =
    report.summary;

  if (total_findings === 0) {
    return (
      <div
        style={{
          marginTop: '1.5rem',
          padding: '0.75rem 1rem',
          background: '#d4f4d4',
          border: '1px solid #0a8',
          borderRadius: 6,
          fontSize: '0.9em',
        }}
      >
        ✅ <strong>Звіт reconciliation</strong>: розбіжностей з 1С/BAS не
        виявлено. Можна спокійно запускати bulk-POST.
      </div>
    );
  }

  // Групуємо findings по source
  const grouped = report.findings.reduce<Record<string, ReconciliationFinding[]>>(
    (acc, f) => {
      const key = f.source;
      if (!acc[key]) acc[key] = [];
      acc[key].push(f);
      return acc;
    },
    {},
  );

  return (
    <div
      style={{
        marginTop: '1.5rem',
        padding: '1rem',
        background: blocking_findings > 0 ? '#fff8e8' : '#fafafa',
        border: `1px solid ${blocking_findings > 0 ? '#e5b800' : '#ccc'}`,
        borderRadius: 6,
      }}
    >
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <div>
          <strong>
            {blocking_findings > 0 ? '⚠️' : 'ℹ️'} Звіт reconciliation:{' '}
            знайдено <strong>{total_findings}</strong> розбіжностей
          </strong>
          <div style={{ fontSize: '0.85em', color: 'var(--muted)', marginTop: '0.25rem' }}>
            Орієнтовний вплив: <strong>{estimated_impact_uah} грн</strong>
            {blocking_findings > 0 && (
              <>
                {' · '}
                <span style={{ color: '#d33' }}>
                  ⚠️ {blocking_findings} серйозн{blocking_findings === 1 ? 'е' : 'их'}{' '}
                  (вплив &gt;100 грн)
                </span>
              </>
            )}
          </div>
        </div>
        <button
          onClick={() => setExpanded((e) => !e)}
          style={{ background: 'transparent', border: '1px solid #ccc', cursor: 'pointer' }}
        >
          {expanded ? 'Згорнути' : 'Розгорнути деталі'}
        </button>
      </div>

      {expanded && (
        <div style={{ marginTop: '1rem' }}>
          <p style={{ fontSize: '0.9em', color: 'var(--muted)', marginBottom: '0.75rem' }}>
            Закон 5: мігровані числа з 1С/BAS не змінюються. Цей звіт
            інформує про <strong>можливі різниці</strong> у нових
            нарахуваннях, які наш сервіс зробить після міграції.
          </p>
          {(Object.entries(grouped) as [ReconciliationSource, ReconciliationFinding[]][]).map(
            ([source, findings]) => {
              const meta = RECON_SOURCE_META[source];
              return (
                <details
                  key={source}
                  open
                  style={{
                    marginBottom: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    background: meta.bgColor,
                    border: `1px solid ${meta.color}`,
                    borderRadius: 4,
                  }}
                >
                  <summary
                    style={{
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      color: meta.color,
                    }}
                  >
                    {meta.icon} {meta.title} ({findings.length})
                  </summary>
                  <div style={{ marginTop: '0.5rem', fontSize: '0.85em' }}>
                    {findings.slice(0, 5).map((f, i) => (
                      <ReconciliationFindingRow
                        key={i}
                        finding={f}
                        isAccepted={acceptedKeys.has(overrideAcceptedKey(f))}
                        onAccept={() => createOverride.mutate(f)}
                        isPending={createOverride.isPending}
                      />
                    ))}
                    {findings.length > 5 && (
                      <p style={{ color: 'var(--muted)', fontStyle: 'italic' }}>
                        … і ще {findings.length - 5} аналогічних знайдень
                      </p>
                    )}
                  </div>
                </details>
              );
            },
          )}
        </div>
      )}
    </div>
  );
}

interface ReconciliationFindingRowProps {
  finding: ReconciliationFinding;
  isAccepted: boolean;
  onAccept: () => void;
  isPending: boolean;
}

function ReconciliationFindingRow({
  finding, isAccepted, onAccept, isPending,
}: ReconciliationFindingRowProps) {
  return (
    <div
      style={{
        padding: '0.5rem',
        background: isAccepted ? '#f0fff0' : '#fff',
        borderRadius: 3,
        marginBottom: '0.25rem',
        border: isAccepted ? '1px solid #0a8' : '1px solid #eee',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <code style={{ fontSize: '0.85em' }}>{finding.field}</code>
        <span style={{ fontSize: '0.85em', color: 'var(--muted)' }}>
          {finding.context}
        </span>
      </div>
      <div style={{ marginTop: '0.25rem', fontSize: '0.85em' }}>
        Наше: <code>{String(finding.our_value)}</code>
        {' · '}
        1С/BAS: <code>{String(finding.their_value)}</code>
        {' · '}
        Вплив: <strong>{finding.difference_amount} грн</strong>
      </div>
      <div style={{ marginTop: '0.25rem', fontSize: '0.85em', color: 'var(--muted)' }}>
        💡 {finding.recommendation}
      </div>
      {/* Block C Порція 3: «Прийняти 1С» кнопка. Невидима для empty target. */}
      {finding.target_table && (
        <div style={{ marginTop: '0.5rem' }}>
          {isAccepted ? (
            <span style={{ color: '#0a8', fontSize: '0.85em' }}>
              ✅ 1С/BAS значення прийнято (recalc використає це)
            </span>
          ) : (
            <button
              type="button"
              onClick={onAccept}
              disabled={isPending}
              title="Створити override: при recalc цього документа використати 1С/BAS значення замість обчисленого"
              style={{
                fontSize: '0.85em',
                padding: '0.25rem 0.5rem',
                background: '#fff',
                border: '1px solid #06c',
                borderRadius: 3,
                cursor: isPending ? 'wait' : 'pointer',
                color: '#06c',
              }}
            >
              {isPending ? '⏳ Зберігаю…' : '✓ Прийняти 1С/BAS'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Звірка ОСВ з 1С (2026-07-19): еталонні обороти Дт×Кт з регістра
// бухгалтерії 1С (секція osv_1c обробки) проти наших acc.entries.
// ============================================================

function fmtUah(s: string): string {
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString('uk-UA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function OsvReconciliationSection({ osv }: { osv: OsvReconciliation }) {
  const [showAllAccounts, setShowAllAccounts] = useState(false);
  const [showPairs, setShowPairs] = useState(false);

  if (osv.error) {
    return (
      <div
        style={{
          marginTop: '1.5rem', padding: '0.75rem 1rem',
          background: '#fdecea', border: '1px solid #d33', borderRadius: 6,
        }}
      >
        ⚠️ <strong>Звірка ОСВ</strong>: {osv.error}
      </div>
    );
  }
  const s = osv.summary;
  if (!s) return null;

  const accounts = osv.accounts ?? [];
  const badAccounts = accounts.filter((a) => !a.ok);
  const shownAccounts = showAllAccounts ? accounts : badAccounts;
  const pairs = osv.pairs ?? [];
  const allOk = s.accounts_diff === 0;

  const th: CSSProperties = {
    textAlign: 'right', padding: '0.3rem 0.5rem',
    borderBottom: '1px solid #ccc', whiteSpace: 'nowrap',
  };
  const td: CSSProperties = {
    textAlign: 'right', padding: '0.25rem 0.5rem',
    borderBottom: '1px solid #eee', whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  };
  const diffStyle = (v: string): CSSProperties =>
    Math.abs(Number(v)) >= 0.01
      ? { ...td, color: '#c00', fontWeight: 600 }
      : { ...td, color: '#0a8' };

  return (
    <div
      style={{
        marginTop: '1.5rem', padding: '1rem',
        background: allOk ? '#d4f4d4' : '#fff8e8',
        border: `1px solid ${allOk ? '#0a8' : '#e5b800'}`,
        borderRadius: 6,
      }}
    >
      <strong>
        {allOk ? '✅' : '⚠️'} Звірка ОСВ з 1С
        {osv.period && (
          <span style={{ fontWeight: 400 }}>
            {' '}({osv.period.from} — {osv.period.to})
          </span>
        )}
      </strong>
      <div style={{ fontSize: '0.85em', color: 'var(--muted)', marginTop: '0.25rem' }}>
        Обороти 1С: <strong>{fmtUah(s.total_1c)}</strong> грн ·{' '}
        наші: <strong>{fmtUah(s.total_ours)}</strong> грн ·{' '}
        різниця: <strong>{fmtUah(s.total_diff)}</strong> грн ·{' '}
        рахунків збігається <strong>{s.accounts_ok}</strong> з{' '}
        <strong>{s.accounts_total}</strong>
      </div>

      {allOk ? (
        <div style={{ marginTop: '0.5rem', fontSize: '0.9em' }}>
          Усі обороти по рахунках збігаються з регістром бухгалтерії 1С
          копійка в копійку.
        </div>
      ) : (
        <>
          <div style={{ marginTop: '0.75rem', overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: '0.82em', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }}>Рахунок</th>
                  <th style={th}>Дт 1С</th>
                  <th style={th}>Дт наші</th>
                  <th style={th}>Δ Дт</th>
                  <th style={th}>Кт 1С</th>
                  <th style={th}>Кт наші</th>
                  <th style={th}>Δ Кт</th>
                </tr>
              </thead>
              <tbody>
                {shownAccounts.map((a) => (
                  <tr key={a.code}>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{a.code}</td>
                    <td style={td}>{fmtUah(a.dt_1c)}</td>
                    <td style={td}>{fmtUah(a.dt_ours)}</td>
                    <td style={diffStyle(a.diff_dt)}>{fmtUah(a.diff_dt)}</td>
                    <td style={td}>{fmtUah(a.kt_1c)}</td>
                    <td style={td}>{fmtUah(a.kt_ours)}</td>
                    <td style={diffStyle(a.diff_kt)}>{fmtUah(a.diff_kt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setShowAllAccounts((v) => !v)}
              style={{ background: 'transparent', border: '1px solid #ccc', cursor: 'pointer', fontSize: '0.85em' }}
            >
              {showAllAccounts
                ? 'лише розбіжності'
                : `показати всі рахунки (${accounts.length})`}
            </button>
            {pairs.length > 0 && (
              <button
                onClick={() => setShowPairs((v) => !v)}
                style={{ background: 'transparent', border: '1px solid #ccc', cursor: 'pointer', fontSize: '0.85em' }}
              >
                {showPairs
                  ? 'сховати кореспонденції'
                  : `кореспонденції з розбіжністю (${pairs.length}${(osv.pairs_truncated ?? 0) > 0 ? '+' : ''})`}
              </button>
            )}
          </div>
          {showPairs && (
            <div style={{ marginTop: '0.5rem', overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: '0.82em' }}>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: 'left' }}>Дт → Кт</th>
                    <th style={th}>1С</th>
                    <th style={th}>наші</th>
                    <th style={th}>Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {pairs.map((p) => (
                    <tr key={`${p.dt}|${p.kt}`}>
                      <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>
                        {p.dt} → {p.kt}
                      </td>
                      <td style={td}>{fmtUah(p.amount_1c)}</td>
                      <td style={td}>{fmtUah(p.amount_ours)}</td>
                      <td style={diffStyle(p.diff)}>{fmtUah(p.diff)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(osv.pairs_truncated ?? 0) > 0 && (
                <div style={{ fontSize: '0.8em', color: 'var(--muted)', marginTop: '0.25rem' }}>
                  … та ще {osv.pairs_truncated} кореспонденцій (показано топ-300 за розбіжністю)
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
